import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { useToast } from "../components/ui";
import { setReviewed } from "../listings/priceCheck/priceCheckApi";
import { requireResponse } from "../core/errors/requireResponse";
import { SILENT } from "./useDashboardData";

/**
 * Every action row's button. The backend says WHAT (`cta.action` + `href` +
 * `entity`); this decides HOW:
 *
 *  open               internal path → router push; absolute URL → new tab
 *  payment_link       fetch the order (GET /api/orders/{reference|id}, the full
 *                     relation tree the request page also uses) and mount the
 *                     existing SendPaymentLinkModal, amount preset to the
 *                     remaining balance (`amount_pence`). If the order can't be
 *                     loaded, fall back to the request's PAYMENTS section.
 *  mark_reviewed      GET /api/listings/{id}?mark_reviewed=1, then mark done
 *  mark_comp_reviewed PUT /api/listings/{id}/skip-comp-check with the WHOLE map
 *                     (fresh listing read first; only the flagged channel set
 *                     true — an omitted channel would be cleared), then done
 *  whatsapp           https://wa.me/<digits> in a new tab (noopener)
 *  book_collection    the collection modal → PUT /api/requests/{id}/collection
 */

const MARKETPLACES = ["chrono", "ebay", "wf", "dao"];

const openExternal = (url) => {
  const w = window.open(url, "_blank", "noopener,noreferrer");
  if (w) w.opener = null;
};

const queryParam = (href, name) => {
  try {
    return new URL(href, "http://x").searchParams.get(name);
  } catch {
    return null;
  }
};

/** The marketplace a comp-flag action is about: entity.mp → ?channel= → key variant. */
export const compChannel = (item) => {
  const fromEntity = item?.entity?.mp || item?.entity?.marketplace;
  if (MARKETPLACES.includes(fromEntity)) return fromEntity;
  const fromHref = queryParam(item?.cta?.href || "", "channel");
  if (MARKETPLACES.includes(fromHref)) return fromHref;
  const last = String(item?.key || "").split(":").pop().replace(/^comp_/, "");
  return MARKETPLACES.includes(last) ? last : null;
};

export const waDigits = (phone) => String(phone || "").replace(/\D/g, "");

const requestPath = (entity = {}, section) => {
  const base =
    entity.request_type === "px-order" && entity.px_request_id
      ? `/request/px-order/${entity.px_request_id}`
      : entity.request_id
        ? `/request/${entity.request_type === "sell" ? "sell" : "order"}/${entity.request_id}`
        : null;
  return base && section ? `${base}?section=${section}` : base;
};

export function useDashboardCta({ markDone, onChanged }) {
  const history = useHistory();
  const toast = useToast();
  const [busyKey, setBusyKey] = useState(null);
  // { order, presetAmountPence, item } while the payment-link modal is open
  const [paymentLink, setPaymentLink] = useState(null);
  // the item whose collection is being booked
  const [collectionFor, setCollectionFor] = useState(null);

  const go = useCallback((href) => {
    if (!href) return false;
    if (/^https?:\/\//i.test(href)) openExternal(href);
    else history.push(href);
    return true;
  }, [history]);

  const withBusy = async (item, fn) => {
    setBusyKey(item.key);
    try {
      await fn();
    } finally {
      setBusyKey(null);
    }
  };

  const openPaymentLink = (item) =>
    withBusy(item, async () => {
      const ref = item.entity?.order_reference || item.entity?.order_id;
      try {
        if (!ref) throw new Error("no order");
        const { data: order } = await axios.get(`/api/orders/${ref}`, SILENT);
        if (!order?.id) throw new Error("no order");
        setPaymentLink({ order, presetAmountPence: item.amount_pence, item });
      } catch {
        const fallback = requestPath(item.entity, "payments") || item.cta?.href;
        toast.warning("Couldn't load the order here — opening its payments instead.");
        go(fallback);
      }
    });

  const markReviewed = (item) =>
    withBusy(item, async () => {
      const id = item.entity?.listing_id;
      try {
        requireResponse(await axios.get(`/api/listings/${id}`, { params: { mark_reviewed: 1 }, ...SILENT }));
        toast.success("Listing marked reviewed");
        await markDone(item, { silent: true });
      } catch {
        toast.error("Couldn't mark the listing reviewed — try again.");
      }
    });

  const markCompReviewed = (item) =>
    withBusy(item, async () => {
      const id = item.entity?.listing_id;
      const mp = compChannel(item);
      if (!id || !mp) {
        toast.error("This price flag doesn't say which marketplace — open the listing instead.");
        return;
      }
      try {
        // Fresh read: the endpoint REPLACES the whole skip map, so every other
        // channel's current flag has to be sent back as it is.
        const { data: listing } = await axios.get(`/api/listings/${id}`, SILENT);
        await setReviewed({ ...listing, id }, mp, true);
        toast.success("Price marked reviewed");
        await markDone(item, { silent: true });
      } catch {
        toast.error("Couldn't mark the price reviewed — try again.");
      }
    });

  const run = useCallback((item) => {
    const cta = item?.cta || {};
    switch (cta.action) {
      case "payment_link":
        return openPaymentLink(item);
      case "mark_reviewed":
        return markReviewed(item);
      case "mark_comp_reviewed":
        return markCompReviewed(item);
      case "whatsapp": {
        const digits = waDigits(item.entity?.phone);
        if (digits) openExternal(`https://wa.me/${digits}`);
        else if (!go(cta.href)) toast.warning("No phone number on this client.");
        return undefined;
      }
      case "book_collection":
        if (!item.entity?.request_id) {
          go(cta.href);
          return undefined;
        }
        setCollectionFor(item);
        return undefined;
      case "open":
      default:
        if (!go(cta.href)) toast.warning("Nothing to open for this action.");
        return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [go, markDone, toast]);

  return {
    run,
    go,
    busyKey,
    paymentLink,
    closePaymentLink: () => {
      setPaymentLink(null);
      onChanged?.();
    },
    collectionFor,
    closeCollection: (saved) => {
      setCollectionFor(null);
      if (saved) {
        toast.success("Collection booked");
        onChanged?.();
      }
    },
  };
}
